import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUser, FiTrendingUp, FiCpu, FiShield, FiZap, FiBook, FiArrowRight } = FiIcons;

const SolutionsPage = () => {
  const [activeTab, setActiveTab] = useState(0);

  const solutions = [
    {
      title: 'IdealMe',
      subtitle: 'AI-Powered Personal Development',
      description: 'Transform your potential with personalized AI coaching that adapts to your unique growth patterns and accelerates your journey to excellence.',
      icon: FiUser,
      features: [
        'Personalized AI coaching',
        'Growth pattern analysis',
        'Real-time feedback',
        'Goal optimization'
      ],
      image: 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=600&h=400&fit=crop'
    },
    {
      title: 'Neuro Mode',
      subtitle: 'Data-Driven Transformation',
      description: 'Unlock insights from your behavioral data to identify breakthrough opportunities and optimize your performance across all dimensions.',
      icon: FiTrendingUp,
      features: [
        'Behavioral analytics',
        'Performance metrics',
        'Predictive insights',
        'Custom dashboards'
      ],
      image: 'https://cqpenfycgbztkozuwfiu.supabase.co/storage/v1/object/sign/logos/fulllogo_transparent%20(1).png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9kZjlmMTQ4My1mMmM0LTRjZTAtOWIxNi01N2E0Njk3ZWIxYjciLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJsb2dvcy9mdWxsbG9nb190cmFuc3BhcmVudCAoMSkucG5nIiwiaWF0IjoxNzU2NTU0OTE3LCJleHAiOjMzMjkyNTU0OTE3fQ.OcCaeoPz9PKtkhHqb7yFIlTrnDoH3elB_VWUR0Bm7DA'
    },
    {
      title: 'AI Coaching Engine',
      subtitle: 'Intelligent Guidance System',
      description: 'Experience next-generation coaching with AI that understands context, emotion, and progress to deliver precisely what you need, when you need it.',
      icon: FiCpu,
      features: [
        'Contextual AI responses',
        'Emotional intelligence',
        'Progress tracking',
        'Adaptive learning'
      ],
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&h=400&fit=crop'
    },
    {
      title: 'On-Demand AI Business Training',
      subtitle: 'Real-Time Learning Solutions',
      description: 'Revolutionary AI-powered training sessions that provide instant guidance, interactive simulations, and actionable insights tailored to your organizational needs.',
      icon: FiZap,
      features: [
        'Real-time AI guidance',
        'Interactive simulations',
        'Organizational customization',
        'Instant performance insights'
      ],
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=400&fit=crop'
    },
    {
      title: 'Asynchronous Training Modules',
      subtitle: 'Self-Paced Excellence',
      description: 'Premium, self-paced training content covering essential business, leadership, and innovation topics. Learn at your own pace, anytime, anywhere.',
      icon: FiBook,
      features: [
        'Self-paced learning',
        'Premium content library',
        'Leadership development',
        'Innovation frameworks'
      ],
      image: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=600&h=400&fit=crop'
    },
    {
      title: 'Privacy-First APIs',
      subtitle: 'Secure Integration Platform',
      description: 'Build transformational applications with our privacy-first APIs that ensure your data remains secure while delivering powerful personalization.',
      icon: FiShield,
      features: [
        'End-to-end encryption',
        'GDPR compliant',
        'Zero-knowledge architecture',
        'Developer-friendly'
      ],
      image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=600&h=400&fit=crop'
    }
  ];

  return (
    <div className="pt-24 pb-16">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center space-y-8"
          >
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="text-5xl md:text-6xl font-bold leading-tight"
            >
              Our <span className="gradient-text">Solutions</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed"
            >
              Cutting-edge AI tools designed to scale personal and organizational transformation
            </motion.p>
          </motion.div>
          {/* Background Elements */}
          <div className="absolute top-1/3 left-1/4 w-64 h-64 bg-primary-orange/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary-orange/3 rounded-full blur-3xl"></div>
        </div>
      </section>

      {/* Solution Tabs */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="space-y-16">
            {/* Tab Navigation */}
            <div className="flex flex-wrap justify-center gap-4">
              {solutions.map((solution, index) => (
                <motion.button
                  key={index}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setActiveTab(index)}
                  className={`flex items-center space-x-3 px-6 py-3 transition-all duration-300 ${
                    activeTab === index
                      ? 'bg-primary-orange text-black'
                      : 'bg-gray-900 text-gray-300 hover:text-white border border-gray-700 hover:border-primary-orange'
                  }`}
                >
                  <SafeIcon icon={solution.icon} className="w-5 h-5" />
                  <span className="font-medium">{solution.title}</span>
                </motion.button>
              ))}
            </div>

            {/* Active Solution Content */}
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="grid md:grid-cols-2 gap-12 items-center"
            >
              {/* Content */}
              <div className="space-y-6 text-left">
                <div className="space-y-4">
                  <h3 className="text-3xl font-bold">{solutions[activeTab].title}</h3>
                  <p className="text-primary-orange font-medium text-lg">
                    {solutions[activeTab].subtitle}
                  </p>
                  <p className="text-gray-300 text-lg leading-relaxed">
                    {solutions[activeTab].description}
                  </p>
                </div>

                {/* Features */}
                <div className="space-y-3">
                  {solutions[activeTab].features.map((feature, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.1, duration: 0.5 }}
                      className="flex items-center space-x-3"
                    >
                      <div className="w-2 h-2 bg-primary-orange rounded-full"></div>
                      <span className="text-gray-300">{feature}</span>
                    </motion.div>
                  ))}
                </div>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-transparent border border-primary-orange text-primary-orange px-8 py-3 font-semibold hover:bg-primary-orange hover:text-black transition-all duration-300"
                >
                  Learn More
                </motion.button>
              </div>

              {/* Visual */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
                className="relative"
              >
                <div className="aspect-video bg-gray-900 tech-border overflow-hidden">
                  <img
                    src={solutions[activeTab].image}
                    alt={solutions[activeTab].title}
                    className="w-full h-full object-cover opacity-80"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                </div>
                {/* Floating Icon */}
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute -top-6 -right-6 w-16 h-16 bg-primary-orange flex items-center justify-center"
                >
                  <SafeIcon icon={solutions[activeTab].icon} className="w-8 h-8 text-black" />
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Training Solutions Highlight */}
      <section className="py-16 bg-gray-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-12"
          >
            <div className="text-center space-y-4">
              <h3 className="text-3xl font-bold">
                Revolutionary <span className="gradient-text">AI Training</span>
              </h3>
              <p className="text-gray-300 text-lg max-w-3xl mx-auto">
                Experience the future of organizational learning with our AI-powered training solutions
              </p>
            </div>

            <div className="glass-effect p-8 space-y-8">
              <div className="grid md:grid-cols-2 gap-8">
                {/* On-Demand Training Card */}
                <motion.div
                  whileHover={{ y: -5 }}
                  className="bg-gray-900/50 p-6 tech-border space-y-4 group"
                >
                  <div className="flex items-center space-x-4">
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      className="w-12 h-12 bg-gradient-to-br from-primary-orange to-primary-dark flex items-center justify-center"
                    >
                      <SafeIcon icon={FiZap} className="w-6 h-6 text-black" />
                    </motion.div>
                    <div>
                      <h4 className="text-xl font-bold group-hover:text-primary-orange transition-colors">
                        On-Demand AI Training
                      </h4>
                      <p className="text-primary-orange font-medium">Real-Time Learning</p>
                    </div>
                  </div>
                  <p className="text-gray-300">
                    Interactive, AI-guided sessions that adapt in real-time to your team's needs and learning pace.
                  </p>
                  <div className="flex justify-end">
                    <motion.button
                      whileHover={{ scale: 1.05, x: 5 }}
                      className="flex items-center space-x-2 text-primary-orange"
                    >
                      <span>Learn more</span>
                      <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
                    </motion.button>
                  </div>
                </motion.div>

                {/* Asynchronous Training Card */}
                <motion.div
                  whileHover={{ y: -5 }}
                  className="bg-gray-900/50 p-6 tech-border space-y-4 group"
                >
                  <div className="flex items-center space-x-4">
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      className="w-12 h-12 bg-gradient-to-br from-primary-orange to-primary-dark flex items-center justify-center"
                    >
                      <SafeIcon icon={FiBook} className="w-6 h-6 text-black" />
                    </motion.div>
                    <div>
                      <h4 className="text-xl font-bold group-hover:text-primary-orange transition-colors">
                        Asynchronous Modules
                      </h4>
                      <p className="text-primary-orange font-medium">Self-Paced Excellence</p>
                    </div>
                  </div>
                  <p className="text-gray-300">
                    Premium content library covering business, leadership, and innovation - available 24/7.
                  </p>
                  <div className="flex justify-end">
                    <motion.button
                      whileHover={{ scale: 1.05, x: 5 }}
                      className="flex items-center space-x-2 text-primary-orange"
                    >
                      <span>Learn more</span>
                      <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
                    </motion.button>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="glass-effect p-12 text-center space-y-8"
          >
            <h2 className="text-3xl font-bold">
              Ready to <span className="gradient-text">Transform</span> Your Organization?
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Book a demo today and discover how our AI-powered solutions can help you achieve unprecedented growth.
            </p>
            <motion.div className="pt-4">
              <motion.a
                href="/booking"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-primary-orange text-black px-8 py-4 font-semibold text-lg hover:bg-primary-dark transition-all duration-200 inline-flex items-center space-x-2"
              >
                <span>Book a Demo</span>
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </motion.a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SolutionsPage;